// CameraCaptureModal
//
// In-app photo capture using getUserMedia. Solves the "Camera button
// does the same thing as Upload on desktop" problem — the old
// implementation used <input type="file" capture="user">, but the
// `capture` attribute is silently ignored by every desktop browser, so
// the button just opened a regular file picker.
//
// This modal:
//   * Requests the device camera via navigator.mediaDevices.getUserMedia
//   * Shows a live preview (mirrored on the user-facing camera, so it
//     feels like a mirror — but saves the un-mirrored image so text in
//     the frame reads correctly)
//   * Lets the user snap a frame, preview it, and either retake or keep
//   * Cleans up the camera stream on close and on unmount (otherwise
//     the green "in use" indicator stays on after the modal closes)
//
// Reusable across photo + Ghana card flows by switching `facingMode`:
//   * 'user'        — selfie camera (default; profile photos)
//   * 'environment' — back camera (Ghana card / documents)
//
// Falls back to a friendly error message when:
//   * the browser doesn't expose getUserMedia (very old browsers, or
//     the page isn't served over HTTPS — getUserMedia is a secure
//     context API)
//   * the user denies the permission prompt
//   * the device has no camera

import React, { useEffect, useRef, useState } from 'react';
import { Camera, RotateCcw, Check, AlertCircle, Upload } from 'lucide-react';
import { Modal, Button } from './ui';

export default function CameraCaptureModal({
  open,
  onClose,
  onCapture,            // (file: File) => void  — JPEG blob wrapped in a File
  facingMode = 'user',  // 'user' = selfie / 'environment' = back camera
  title      = 'Take a photo',
  // Optional fallback: when getUserMedia is unavailable (e.g. http://
  // dev environments) we surface a "Pick a file instead" button that
  // triggers a hidden <input type="file">. Set to false to hide it.
  allowFileFallback = true,
}) {
  const videoRef    = useRef(null);
  const canvasRef   = useRef(null);
  const streamRef   = useRef(null);
  const fileInputRef = useRef(null);

  const [error,    setError]    = useState('');
  const [starting, setStarting] = useState(false);
  const [preview,  setPreview]  = useState(null); // captured data-URL while user decides retake/use

  // ── Camera lifecycle ────────────────────────────────────────────
  // Start the stream when the modal opens; stop it when it closes or
  // when the component unmounts. Tracking with a `cancelled` flag so
  // that if the user closes the modal *while* getUserMedia is still
  // resolving, we don't end up with an orphan stream.
  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    setError('');
    setPreview(null);
    setStarting(true);

    const start = async () => {
      try {
        if (!navigator.mediaDevices?.getUserMedia) {
          // Most common cause is http:// origin in dev. Mention both.
          throw new Error(
            'Camera access requires HTTPS and a supported browser.',
          );
        }
        const stream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode,
            width:  { ideal: 1280 },
            height: { ideal: 720 },
          },
          audio: false,
        });
        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        streamRef.current = stream;
        if (videoRef.current) videoRef.current.srcObject = stream;
      } catch (e) {
        if (cancelled) return;
        // Map the standard error names to language a user can act on.
        if (e?.name === 'NotAllowedError' || e?.name === 'SecurityError') {
          setError('Camera permission denied. Allow camera access in your browser settings and try again.');
        } else if (e?.name === 'NotFoundError' || e?.name === 'OverconstrainedError') {
          setError('No camera found on this device.');
        } else if (e?.name === 'NotReadableError') {
          setError('Camera is already in use by another application.');
        } else {
          setError(e?.message || 'Could not start the camera.');
        }
      } finally {
        if (!cancelled) setStarting(false);
      }
    };
    start();

    return () => {
      cancelled = true;
      const s = streamRef.current;
      if (s) {
        s.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
      }
    };
  }, [open, facingMode]);

  // ── Snap / retake / use ─────────────────────────────────────────
  const handleSnap = () => {
    const video  = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas || !video.videoWidth) return;

    // Match the canvas to the camera's intrinsic resolution. This
    // preserves quality regardless of how big or small the preview
    // box was rendered.
    canvas.width  = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext('2d');

    // Preview is mirrored for the user-facing camera via CSS, but we
    // intentionally draw the un-mirrored frame to the canvas so saved
    // photos show text correctly and people aren't recorded as if in a
    // mirror.
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

    setPreview(canvas.toDataURL('image/jpeg', 0.92));
  };

  const handleRetake = () => setPreview(null);

  const handleUse = async () => {
    if (!preview) return;
    // Convert data URL → Blob → File so callers can use the file in
    // the same upload flow as their <input type="file"> path.
    const blob = await (await fetch(preview)).blob();
    const file = new File([blob], `photo-${Date.now()}.jpg`, { type: 'image/jpeg' });
    onCapture?.(file);
    onClose?.();
  };

  // ── File fallback ───────────────────────────────────────────────
  const handlePickFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    onCapture?.(f);
    onClose?.();
  };

  // ── Render ──────────────────────────────────────────────────────
  const cameraReady = !error && !starting;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      size="md"
      footer={
        preview ? (
          <>
            <Button variant="ghost" icon={RotateCcw} onClick={handleRetake}>Retake</Button>
            <Button icon={Check} onClick={handleUse}>Use photo</Button>
          </>
        ) : (
          <>
            <Button variant="ghost" onClick={onClose}>Cancel</Button>
            {error && allowFileFallback ? (
              <Button icon={Upload} onClick={() => fileInputRef.current?.click()}>
                Pick a file instead
              </Button>
            ) : (
              <Button icon={Camera} onClick={handleSnap} disabled={!cameraReady}>
                {starting ? 'Starting camera…' : 'Snap photo'}
              </Button>
            )}
          </>
        )
      }
    >
      <div className="space-y-3">
        {error && (
          <div className="flex items-start gap-2 p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-xs text-red-700 dark:text-red-400">
            <AlertCircle size={14} className="flex-shrink-0 mt-0.5" />
            <span>{error}</span>
          </div>
        )}

        <div className="relative aspect-video w-full rounded-xl overflow-hidden bg-slate-900 flex items-center justify-center">
          {preview ? (
            <img src={preview} alt="" className="w-full h-full object-contain" />
          ) : (
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              className={`w-full h-full object-cover ${facingMode === 'user' ? 'scale-x-[-1]' : ''}`}
            />
          )}
          {starting && !error && !preview && (
            <p className="absolute text-white/80 text-xs">Starting camera…</p>
          )}
        </div>

        {/* Hidden canvas — only used to snapshot the current video frame. */}
        <canvas ref={canvasRef} className="hidden" />

        {/* Hidden file fallback — surfaced via the footer button when
            getUserMedia isn't available. */}
        {allowFileFallback && (
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            // capture is still useful on mobile, even as a fallback —
            // it nudges the OS toward the camera roll vs. all files.
            capture={facingMode}
            className="hidden"
            onChange={handlePickFile}
          />
        )}
      </div>
    </Modal>
  );
}
